import { FC, lazy, Suspense, useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { IconPaths, LocalVideoChamada, OrigemPipChamada } from '_metronic/helpers/enumsUI'
import { useT } from '_metronic/i18n/Metronici18n'
import { Button } from 'app/components/Button/Button'
import { LoadingIndicator } from 'app/components/Loading/LoadingIndicator'
import { useJanelaPip } from 'app/helpers/hooks/useJanelaPip'
import { MiniPlayer } from './components/MiniPlayer'
import { DadosChamada, dimensoesJanelaPip } from './core/_models'
import { proporcaoDoVideoDoPaciente, REGRAS_PIP } from './core/regrasDoPlayer'

// Sob demanda: não acopla o SDK do Agora ao PrivateRoutes (diretriz §5.4). Hoje o SDK já está no chunk
// principal por outros imports estáticos (spec, seção "Bundle"); o lazy prepara para rotas lazy.
const VideoAtendimentoAgora = lazy(() =>
  import('../atendimento-video/video-atendimento-agora').then((modulo) => ({
    default: modulo.VideoAtendimentoAgora,
  }))
)

type ParamsHost = {
  /** Há chamada ativa? Sem chamada, sair da tela não leva o nó ao mini-player nem ao PiP. */
  ativo: boolean
  /** O vídeo saiu da tela do atendimento (o provider fecha o chat — contrato 2 da spec). */
  aoSairDaTela: () => void
  /** O médico fechou o PiP fora da tela do atendimento: o provider pergunta se ele volta ou sai da chamada. */
  aoFecharPipForaDaTela: () => void
}

export type HostDaChamada = {
  /** Nó DOM fixo onde o player é montado uma única vez. */
  no: HTMLElement
  /** Onde o cabeçalho da janela PiP (paciente e protocolo) é renderizado. */
  cabecalhoPip: HTMLElement
  localDoVideo: LocalVideoChamada
  pipSuportado: boolean
  /** A tela do atendimento está montada (lido na hora, não do último render). */
  naTela: () => boolean
  /** Callback ref estável do encaixe na tela do atendimento. */
  registrarEncaixe: (el: HTMLElement | null) => void
  /** Callback ref estável do container do mini-player. */
  registrarMini: (el: HTMLElement | null) => void
  /** Leva o vídeo para o PiP; precisa do gesto do usuário ou do disparo do PiP automático. */
  abrirPip: (origem: OrigemPipChamada) => void
  /** Nó de volta à aba e PiP fechado, sem encerrar a chamada. */
  devolverEFechar: () => void
  /** Encerramento: marca inativa de forma síncrona e devolve o nó. */
  desativar: () => void
}

const criarElemento = (classes: string) => {
  const el = document.createElement('div')
  el.className = classes
  return el
}

/**
 * Dono do nó DOM fixo onde o player é montado UMA vez. Move o nó entre o encaixe da tela do atendimento,
 * a janela Document PiP e o mini-player — mover um <video> não interrompe a reprodução (spike, seção 7).
 */
export const useHostDaChamada = ({
  ativo,
  aoSairDaTela,
  aoFecharPipForaDaTela,
}: ParamsHost): HostDaChamada => {
  const [no] = useState(() => criarElemento('w-100 h-100'))
  // Conteúdo da janela PiP: cabeçalho fixo em cima e o vídeo ocupando o resto.
  const [{ conteinerPip, cabecalhoPip, areaVideoPip }] = useState(() => {
    const conteiner = criarElemento('d-flex flex-column h-100 bg-black')
    const cabecalho = criarElemento('flex-shrink-0')
    const area = criarElemento('flex-grow-1 position-relative overflow-hidden')
    conteiner.append(cabecalho, area)
    return { conteinerPip: conteiner, cabecalhoPip: cabecalho, areaVideoPip: area }
  })
  const encaixeRef = useRef<HTMLElement | null>(null)
  const miniRef = useRef<HTMLElement | null>(null)
  const ativoRef = useRef(ativo)
  const origemPipRef = useRef(OrigemPipChamada.Gesto)
  const aoSairDaTelaRef = useRef(aoSairDaTela)
  const aoFecharPipForaDaTelaRef = useRef(aoFecharPipForaDaTela)
  const [localDoVideo, setLocalDoVideo] = useState(LocalVideoChamada.Tela)

  useLayoutEffect(() => {
    ativoRef.current = ativo
    aoSairDaTelaRef.current = aoSairDaTela
    aoFecharPipForaDaTelaRef.current = aoFecharPipForaDaTela
  })

  /** Devolve o nó para a aba: o encaixe, se a tela do atendimento estiver aberta; senão, o mini-player. */
  const colocarNaAba = useCallback(() => {
    const destino = encaixeRef.current ?? miniRef.current
    if (!destino) return
    destino.appendChild(no)
    setLocalDoVideo(encaixeRef.current ? LocalVideoChamada.Tela : LocalVideoChamada.Mini)
  }, [no])

  /**
   * A janela PiP fechou sem ser por código nosso (X, Esc, "voltar à aba" ou o fechamento automático do
   * Chrome). O nó sai do documento que está morrendo de forma síncrona; depois decide-se a chamada:
   * - na tela do atendimento: o vídeo só volta ao lugar;
   * - fora dela: avisa o provider, que pergunta ao médico se volta ao atendimento ou sai da chamada —
   *   exceto quando foi o Chrome que fechou o PiP automático porque o médico voltou à aba (esse fechamento
   *   não foi pedido; o vídeo fica no mini-player). O navegador não informa quem fechou: com o PiP
   *   automático, decide-se no próximo tick pela visibilidade da aba.
   */
  const aoFecharPip = useCallback(() => {
    colocarNaAba()
    if (encaixeRef.current) return
    if (origemPipRef.current === OrigemPipChamada.Gesto) {
      aoFecharPipForaDaTelaRef.current()
      return
    }
    setTimeout(() => {
      if (document.visibilityState === 'hidden' && !encaixeRef.current) {
        aoFecharPipForaDaTelaRef.current()
      }
    }, 0)
  }, [colocarNaAba])

  const pip = useJanelaPip({ onFechar: aoFecharPip })

  const abrirPip = useCallback(
    (origem: OrigemPipChamada) => {
      if (!ativoRef.current || !pip.suportado) return
      origemPipRef.current = origem
      void pip
        // O "voltar à aba" nativo fecha a janela como o X; o cabeçalho tem o nosso, que volta de fato.
        // Na proporção da câmera do paciente: sem faixas pretas em volta do vídeo.
        .abrir(conteinerPip, {
          ...dimensoesJanelaPip(proporcaoDoVideoDoPaciente(no)),
          regrasExtras: REGRAS_PIP,
          ocultarVoltarParaAba: true,
        })
        .then((aberta) => {
          if (!aberta) return
          // A janela abre de forma assíncrona: se a chamada encerrou nesse meio-tempo, não deixa um PiP vazio.
          if (!ativoRef.current) {
            pip.fechar()
            return
          }
          // Só agora o vídeo sai da aba: até a janela existir, ele continua visível onde estava.
          areaVideoPip.appendChild(no)
          setLocalDoVideo(LocalVideoChamada.Pip)
        })
    },
    [no, pip, conteinerPip, areaVideoPip]
  )

  const registrarEncaixe = useCallback(
    (el: HTMLElement | null) => {
      encaixeRef.current = el
      if (el) {
        el.appendChild(no)
        pip.fechar()
        setLocalDoVideo(LocalVideoChamada.Tela)
        return
      }
      if (!ativoRef.current) return
      aoSairDaTelaRef.current()
      if (pip.aberta()) return
      // Primeiro, síncrono, para o mini-player: o nó nunca fica fora do documento (contrato 2).
      colocarNaAba()
      // O clique que causou a navegação ainda vale como gesto (~5 s): aproveita para abrir o PiP.
      if (navigator.userActivation?.isActive) abrirPip(OrigemPipChamada.Gesto)
    },
    [no, pip, colocarNaAba, abrirPip]
  )

  const registrarMini = useCallback((el: HTMLElement | null) => {
    miniRef.current = el
  }, [])

  const naTela = useCallback(() => encaixeRef.current !== null, [])

  /** Nó de volta à aba, depois fecha o PiP sem callback (contrato 3). */
  const devolverEFechar = useCallback(() => {
    colocarNaAba()
    pip.fechar()
  }, [colocarNaAba, pip])

  /**
   * Encerramento: marca a chamada como inativa de forma SÍNCRONA antes de qualquer atualização de estado.
   * Encerrar e navegar caem no mesmo commit; o encaixe é desanexado antes do layout effect que atualiza
   * `ativoRef` — sem isto, sair da tela com o gesto do último clique ainda ativo abriria um PiP vazio.
   */
  const desativar = useCallback(() => {
    ativoRef.current = false
    devolverEFechar()
  }, [devolverEFechar])

  return {
    no,
    cabecalhoPip,
    localDoVideo,
    pipSuportado: pip.suportado,
    naTela,
    registrarEncaixe,
    registrarMini,
    abrirPip,
    devolverEFechar,
    desativar,
  }
}

type PropsHost = {
  no: HTMLElement
  cabecalhoPip: HTMLElement
  dados: DadosChamada | null
  chatAberto: boolean
  setChatAberto: (aberto: boolean) => void
  aoMudarParticipantes: (quantidade: number) => void
  miniPlayerVisivel: boolean
  registrarMini: (el: HTMLElement | null) => void
  /** "Voltar ao atendimento" do cabeçalho do PiP: traz a aba para a frente na tela da chamada. */
  aoVoltar: () => void
}

/** Porta o player existente para o nó fixo, o cabeçalho para a janela PiP e renderiza o mini-player. */
export const ChamadaAtivaHost: FC<PropsHost> = ({
  no,
  cabecalhoPip,
  dados,
  chatAberto,
  setChatAberto,
  aoMudarParticipantes,
  miniPlayerVisivel,
  registrarMini,
  aoVoltar,
}) => {
  const { t } = useT()

  // Memoizado: digitar na anamnese ou mover o nó não re-renderiza o player. Props explícitas: `protocolo`
  // é do cabeçalho, não do player.
  const player = useMemo(
    () =>
      dados && (
        <Suspense fallback={<LoadingIndicator />}>
          <VideoAtendimentoAgora
            idAtendimento={dados.idAtendimento}
            idPessoa={dados.idPessoa}
            isPaciente={dados.isPaciente}
            pacienteNome={dados.pacienteNome}
            chatOpen={chatAberto}
            setChatOpen={setChatAberto}
            handleNewUser={aoMudarParticipantes}
          />
        </Suspense>
      ),
    [dados, chatAberto, setChatAberto, aoMudarParticipantes]
  )

  // "Nome · sexo · idade" em cima e o protocolo embaixo; do outro lado, o "Voltar ao atendimento" — o único
  // elemento clicável da janela (o vídeo não responde a clique). Altura fixa: entra no tamanho da janela.
  const cabecalho = dados && (
    <div className='d-flex align-items-center gap-3 h-60px px-4 bg-body border-bottom'>
      <div className='d-flex flex-column flex-grow-1 overflow-hidden'>
        <span className='text-gray-700 text-truncate'>
          <span className='fw-bold text-gray-900'>{dados.pacienteNome}</span>
          {dados.sexoIdade && ` · ${dados.sexoIdade}`}
        </span>
        {dados.protocolo && (
          <span className='fs-7 text-gray-700 text-truncate'>
            {t('atendimento.chamada.protocolo', { protocolo: dados.protocolo })}
          </span>
        )}
      </div>
      <Button
        className='btn-secondary flex-shrink-0'
        size='btn-sm'
        icon={IconPaths.ArrowLeft}
        onClick={aoVoltar}
      >
        {t('atendimento.chamada.voltar')}
      </Button>
    </div>
  )

  return (
    <>
      {player && createPortal(player, no)}
      {cabecalho && createPortal(cabecalho, cabecalhoPip)}
      <MiniPlayer
        atendimentoId={dados?.idAtendimento ?? null}
        visivel={miniPlayerVisivel}
        registrar={registrarMini}
      />
    </>
  )
}
